import { omit } from '../../utils/props';
import { isBuyingButtonProps } from '../../utils/purchaseMotionTypeGuards';
import { ErrorBoundary, ErrorBoundaryNames } from '../ErrorBoundary';
import { AssignSeatButton } from '../PurchaseMotions/AssignSeatButton/AssignSeatButton';
import { BuyingButton } from '../PurchaseMotions/BuyingButton/BuyingButton';
import { PricingPageRedirectButton } from '../PurchaseMotions/PricingPageRedirectButton/PricingPageRedirectButton';
import { RequestProductButton } from '../PurchaseMotions/RequestProductButton/RequestProductButton';
import { RequestSeatButton } from '../PurchaseMotions/RequestSeatButton/RequestSeatButton';
import { TalkToSalesButton } from '../PurchaseMotions/TalkToSalesButton/TalkToSalesButton';
import { TrialButton } from '../PurchaseMotions/TrialButton/TrialButton';
import { ViewAccountButton } from '../PurchaseMotions/ViewAccountButton/ViewAccountButton';
import { jsx as _jsx } from "react/jsx-runtime";
export const PurchaseMotionCTABase = ({
  apiName,
  ctaProps,
  level,
  location,
  purchaseMotion,
  uiButtonProps = {},
  trackingData,
  onClick,
  upgradeConfig,
  children,
  'data-test-id': dataTestId,
  'data-selenium-id': dataSeleniumId
}) => {
  //default to primary for first button, secondary for others
  uiButtonProps = Object.assign({
    use: level && level > 0 ? 'secondary' : 'primary'
  }, uiButtonProps);
  const backwardsCompatiblePurchaseMotion = returnPurchaseMotion(purchaseMotion);
  // remove props that are not allowed to be passed to the underlying button component
  const filteredUiButtonProps = omit(uiButtonProps, ['children', 'className', 'external', 'href', 'onClick', 'style']);
  switch (backwardsCompatiblePurchaseMotion) {
    case 'ASSIGN_SEAT':
      return /*#__PURE__*/_jsx(AssignSeatButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'BUY_ADDITIONAL_CONTACTS':
    case 'BUY_ADDITIONAL_SEATS':
    case 'BUY_TOUCHLESS':
      if (isBuyingButtonProps(ctaProps)) {
        return /*#__PURE__*/_jsx(BuyingButton, Object.assign({
          purchaseMotion: backwardsCompatiblePurchaseMotion,
          apiName: apiName,
          level: level,
          location: location,
          trackingData: trackingData,
          upgradeConfig: upgradeConfig,
          "data-test-id": dataTestId,
          "data-selenium-id": dataSeleniumId,
          uiButtonProps: filteredUiButtonProps,
          onClick: onClick
        }, ctaProps, {
          children: children
        }));
      } else {
        throw new Error('ctaProps is not of type BuyingButtonProps');
      }
    case 'PRICING_PAGE_REDIRECT':
      return /*#__PURE__*/_jsx(PricingPageRedirectButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_SEAT':
      return /*#__PURE__*/_jsx(RequestSeatButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'REQUEST_ACCESS':
      return /*#__PURE__*/_jsx(RequestProductButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: true
      }, ctaProps, {
        children: children
      }));
    case 'TALK_TO_SALES_FALLBACK':
      return /*#__PURE__*/_jsx(TalkToSalesButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick,
        isAssignable: false
      }, ctaProps, {
        children: children
      }));
    case 'TRIAL':
      return /*#__PURE__*/_jsx(TrialButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    case 'VIEW_ACCOUNT':
      return /*#__PURE__*/_jsx(ViewAccountButton, Object.assign({
        apiName: apiName,
        level: level,
        location: location,
        trackingData: trackingData,
        upgradeConfig: upgradeConfig,
        "data-test-id": dataTestId,
        "data-selenium-id": dataSeleniumId,
        uiButtonProps: filteredUiButtonProps,
        onClick: onClick
      }, ctaProps, {
        children: children
      }));
    default:
      return null;
  }
};
export const PurchaseMotionCTA = props => /*#__PURE__*/_jsx(ErrorBoundary, {
  boundaryName: ErrorBoundaryNames.PURCHASE_MOTION_CTA,
  children: /*#__PURE__*/_jsx(PurchaseMotionCTABase, Object.assign({}, props))
});
function returnPurchaseMotion(purchaseMotion) {
  switch (purchaseMotion) {
    case 'assignSeat':
      return 'ASSIGN_SEAT';
    case 'buyTouchless':
      return 'BUY_TOUCHLESS';
    case 'buyAdditionalSeats':
      return 'BUY_ADDITIONAL_SEATS';
    case 'buyAdditionalContacts':
      return 'BUY_ADDITIONAL_CONTACTS';
    case 'pricingPageRedirect':
      return 'PRICING_PAGE_REDIRECT';
    case 'requestAccess':
      return 'REQUEST_ACCESS';
    case 'requestSeat':
      return 'REQUEST_SEAT';
    case 'talkToSales':
      return 'TALK_TO_SALES';
    case 'talkToSalesFallback':
      return 'TALK_TO_SALES_FALLBACK';
    case 'trial':
      return 'TRIAL';
    case 'viewAccount':
      return 'VIEW_ACCOUNT';
    default:
      return purchaseMotion;
  }
}